import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PatientCommsTabContentV2,
  PatientProfilePlaceholder,
  PatientProfileSubNav,
  PatientProfileTabContentV2,
  PatientProfileTopBarActions,
  getPatientInsuranceV2Bundle,
  getPatientInvisalignBundle,
  PatientTreatmentTabContent,
  PatientInsuranceTabContentV2,
  PatientFormsTabContent,
  PatientInvisalignTabContent,
  printInsuranceSummary,
  Button,
  CallOutcomeModal,
  useToast,
  type PatientProfileTab,
  type PfPatientFormRecord,
} from '@elva/ui';
import { useWorkspaceLabel } from '@elva/platform';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { ElvaPageLoader } from '@/components/ElvaPageLoader';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import { extractApiErrorMessage } from '@/api/errors';
import { adaptAssignmentSyncStatusMap } from '@/features/forms/adapters/formsAdapters';
import { useFormAssignments } from '@/features/forms/hooks/useFormAssignments';
import { deliverFormReminder, reminderTargetForPatientForm } from '@/features/forms/deliverFormReminder';
import { useResendAssignment, useResendSubmission } from '@/features/forms/hooks/useFormSubmission';
import { useTpiCheatsheet } from '../../tpi/hooks/useTpiCheatsheet';
import { PatientFormDetailDrawer } from '../components/PatientFormDetailDrawer';
import {
  adaptPatientCommsV2,
  adaptPatientForms,
  adaptPatientProfile,
  adaptPatientTreatment,
} from '../adapters/patientAdapters';
import { usePatientCommunications } from '../hooks/usePatientCommunications';
import { usePatientForms } from '../hooks/usePatientForms';
import { usePatientProfile } from '../hooks/usePatientProfile';
import { useProducerOutputs } from '../hooks/useProducerOutputs';
import { parsePatientProfileTab, patientProfilePath } from '../utils/patientProfileRoutes';
import type { TpPatientTreatmentBundle } from '@elva/ui';

/** Plan value and procedure names from the cheatsheet, for the call-outcome modal. */
function callCaseText(bundle: TpPatientTreatmentBundle): string {
  const planValue = bundle.treatments.reduce((n, t) => n + t.fee, 0);
  const names = bundle.treatments.map((t) => t.description).filter((name) => name.length > 0).join(' + ');
  if (planValue <= 0) return names || 'Treatment plan';
  return names ? `$${planValue.toLocaleString()} · ${names}` : `$${planValue.toLocaleString()}`;
}

export function PatientProfilePage() {
  const workspaceLabel = useWorkspaceLabel();
  const navigate = useNavigate();
  const { patientId, tab: tabParam } = useParams<{ patientId: string; tab: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parsePatientProfileTab(tabParam);
  const numericId = patientId ? Number(patientId) : null;
  const validId = numericId != null && !Number.isNaN(numericId) ? numericId : null;

  const { toast } = useToast();
  const [selectedForm, setSelectedForm] = useState<PfPatientFormRecord | null>(null);
  const [reverifyingPlanId, setReverifyingPlanId] = useState<string | null>(null);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const resendSubmission = useResendSubmission(validId);
  const resendAssignment = useResendAssignment(validId);

  const { data: profile, isLoading, isError } = usePatientProfile(validId);
  const { data: outputs } = useProducerOutputs(validId);
  const { data: commsData } = usePatientCommunications(validId);
  const { data: cheatsheet } = useTpiCheatsheet(validId);
  const formsEnabled = tab === 'Forms';
  const {
    data: formsData,
    isLoading: formsLoading,
    isError: formsError,
  } = usePatientForms(validId, formsEnabled);
  const { data: assignmentsData } = useFormAssignments({ enabled: formsEnabled });

  const patient = useMemo(
    () => (profile ? adaptPatientProfile(profile, outputs) : null),
    [profile, outputs],
  );

  const commsBundle = useMemo(() => adaptPatientCommsV2(commsData), [commsData]);

  const insuranceBundle = useMemo(
    () => getPatientInsuranceV2Bundle(patient?.id ?? ''),
    [patient?.id],
  );

  const treatmentBundle = useMemo(
    () => adaptPatientTreatment(cheatsheet, outputs),
    [cheatsheet, outputs],
  );

  const invisalignBundle = useMemo(
    () => getPatientInvisalignBundle(patient?.id ?? ''),
    [patient?.id],
  );

  const syncStatusByAssignmentId = useMemo(
    () => adaptAssignmentSyncStatusMap(assignmentsData),
    [assignmentsData],
  );

  const formsBundle = useMemo(
    () => adaptPatientForms(formsData, syncStatusByAssignmentId),
    [formsData, syncStatusByAssignmentId],
  );

  // Deep link from the Sent forms queue (?assignment=<id>): open that form's
  // preview drawer as soon as its data is in, then drop the param so closing
  // and reopening the drawer behaves normally afterwards.
  const assignmentParam = searchParams.get('assignment');
  useEffect(() => {
    if (!assignmentParam) return;
    const match = formsBundle.forms.find(
      (f) => f.assignmentId === assignmentParam || f.id === assignmentParam || f.submissionId === assignmentParam,
    );
    if (!match) return;
    setSelectedForm(match);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('assignment');
        return next;
      },
      { replace: true },
    );
  }, [assignmentParam, formsBundle.forms, setSearchParams]);

  usePageTopBar({
    ...(patient
      ? { breadcrumb: [workspaceLabel, 'Patients', patient.name, ...(tab ? [tab] : [])] }
      : {}),
    actions:
      patient && tab === 'Insurance' ? (
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            iconLeft="doc"
            onClick={() => {
              printInsuranceSummary(getPatientInsuranceV2Bundle(patient.id), {
                patientName: patient.name,
              });
            }}
          >
            Print Insurance Summary
          </Button>
          <PatientProfileTopBarActions />
        </div>
      ) : patient && tab === 'Treatments' ? (
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" iconLeft="doc" onClick={() => { window.print(); }}>Print</Button>
          <Button variant="primary" size="sm" iconLeft="phone" onClick={() => { setLogCallOpen(true); }}>Log the call</Button>
        </div>
      ) : (
        <PatientProfileTopBarActions />
      ),
  });

  if (patientId && tabParam && !tab) {
    return <Navigate to={patientProfilePath(patientId, 'Profile')} replace />;
  }

  if (isLoading) {
    return <ElvaPageLoader />;
  }

  if (isError || !patient || !tab) {
    return <ElvaSectionState isError>{null}</ElvaSectionState>;
  }

  const handleTabChange = (next: PatientProfileTab) => {
    if (!patientId) return;
    navigate(patientProfilePath(patientId, next));
  };

  const handleSendReminder = async (form: PfPatientFormRecord) => {
    try {
      await deliverFormReminder(reminderTargetForPatientForm(form, assignmentsData, validId), {
        resendSubmission: (submissionId, channel) =>
          resendSubmission.mutateAsync({ submissionId, body: { channel } }),
        resendAssignment: (assignmentId, channel) =>
          resendAssignment.mutateAsync({ assignmentId, body: { channel } }),
      });
      toast({ title: 'Sent by text + email', variant: 'success' });
    } catch (error) {
      toast({ title: 'Resend failed', description: extractApiErrorMessage(error), variant: 'error' });
    }
  };

  return (
    <div className="pp-root v6-root relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="pp-glow" aria-hidden />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <PatientProfileSubNav
          tab={tab}
          onTabChange={handleTabChange}
          hrefFor={(t) => patientProfilePath(patientId ?? '', t)}
        />
        <main className="pp-scroll relative z-[1] mx-auto min-h-0 w-full max-w-[1240px] flex-1 overflow-y-auto px-[30px] pb-11 pt-6">
          {tab === 'Profile' ? (
            <PatientProfileTabContentV2
              patient={patient}
              onOpenTab={handleTabChange}
              onOpenLanguages={() => {
                void navigate('/settings/ai/general#languages');
              }}
              onSelectPatient={(id) => {
                void navigate(patientProfilePath(id, 'Profile'));
              }}
              onSendMessage={() => {
                toast({ title: 'Message sent', variant: 'success' });
              }}
            />
          ) : tab === 'Communications' ? (
            <PatientCommsTabContentV2 bundle={commsBundle} />
          ) : tab === 'Treatments' ? (
            <PatientTreatmentTabContent
              patient={patient}
              bundle={treatmentBundle}
              onLogCall={() => {
                setLogCallOpen(true);
              }}
              onPreviewPlan={() => {
                window.open('/p/K7q9Xm2vR4tBw8LpZ3nQ5d?state=plan&staff=1', '_blank');
              }}
              needsChoice={new URLSearchParams(window.location.search).get('choice') === '1'}
            />
          ) : tab === 'Insurance' ? (
            <PatientInsuranceTabContentV2
              bundle={insuranceBundle}
              reverifyingPlanId={reverifyingPlanId}
              onReVerify={(planId) => {
                setReverifyingPlanId(planId);
                window.setTimeout(() => {
                  setReverifyingPlanId(null);
                }, 1600);
              }}
            />
          ) : tab === 'Forms' ? (
            <ElvaSectionState isLoading={formsLoading} isError={formsError}>
              <PatientFormsTabContent
                patient={patient}
                bundle={formsBundle}
                onPreview={setSelectedForm}
                onSendReminder={(form) => {
                  void handleSendReminder(form);
                }}
              />
            </ElvaSectionState>
          ) : tab === 'Invisalign' ? (
            <PatientInvisalignTabContent
              patient={patient}
              bundle={invisalignBundle}
              onBookConsult={() => {
                toast({ title: 'Book consult — Aligner consult · 45 min · Dr. Carter', variant: 'success' });
              }}
              onPreviewStep={(step) => { void navigate(`/settings/ai/invisalign/programme/${step}`); }}
            />
          ) : (
            <PatientProfilePlaceholder tab={tab} />
          )}
        </main>
      </div>
      <PatientFormDetailDrawer
        form={selectedForm}
        patientId={validId}
        onClose={() => setSelectedForm(null)}
      />
      <CallOutcomeModal
        open={logCallOpen}
        subject={{
          name: patient.name,
          caseText: callCaseText(treatmentBundle),
          when: 'called just now',
        }}
        onClose={() => { setLogCallOpen(false); }}
        onSave={(outcomeKey) => {
          setLogCallOpen(false);
          toast({ title: `Call logged — ${outcomeKey}`, variant: 'success' });
        }}
      />
    </div>
  );
}
